import { defineElement, FC, useBindMethod, useElement, useListen, useProp } from '@atomify/hooks';
import { Fragment, h } from '@atomify/jsx';
import { BPDInput } from '@atoms/form-elements';
import { CheckboxElement } from '@atoms/form-elements/checkbox/checkbox';
import {
    AreaSubscriptionListCard,
    TOGGLE_INPUT_HOOK,
} from '@features/area-subscription/components';
import { areaSubscriptionStore } from '@features/area-subscription/store';
import {
    AreaSubscriptionListCardInterface,
    AreaSubscriptionListElement,
    AreaSubscriptionListHeaderInterface,
    AreaSubscriptionListItemClickHandler,
    AreaSubscriptionListItemType,
    AreaSubscriptionListProjectPhaseData,
    AreaSubscriptionListPropertyTypeData,
} from '@features/area-subscription/types';
import { COLOR_CLASSES } from '@utilities/api-formatters';

const HIDDEN_CLASS = 'u-hidden';

const AreaSubscriptionList: FC<AreaSubscriptionListElement> = ({ element }) => {
    const [itemType] = useProp<AreaSubscriptionListItemType>('itemType');
    const [selectable] = useProp<boolean>('selectable', false);
    const [hasSelectAll] = useProp<boolean>('hasSelectAll', false);
    const [onItemClick] = useProp<AreaSubscriptionListItemClickHandler | null>('onItemClick', null);

    let _data: { card: HTMLButtonElement; item: AreaSubscriptionListCardInterface }[] = [];

    useElement<HTMLSpanElement>(`[js-hook-area-subscription-list-header]`, { as: 'listHeader' });
    useElement<HTMLSpanElement>(`[js-hook-area-subscription-list-title]`, { as: 'listTitle' });
    useElement<HTMLSpanElement>(`[js-hook-area-subscription-list]`, { as: 'list' });

    useElement<BPDInput>('[js-hook-area-subscription-list-select-all-toggle]', {
        as: 'selectAllToggle',
    });
    useElement<HTMLSpanElement>('[js-hook-area-subscription-list-select-all-toggle-label]', {
        as: 'selectAllToggleLabel',
    });
    const selectAllToggle = useElement<HTMLInputElement>(
        '[js-hook-area-subscription-list-select-all-toggle-inner]',
        { as: 'selectAllToggleInput' },
    );

    useListen(selectAllToggle, 'change', () => {
        const isAllSelected = selectAllToggle.current!.checked;
        let selected: string[];

        switch (itemType) {
            case 'projectPhase':
                selected = areaSubscriptionStore.getState().selectedProjectPhases;
                break;
            case 'propertyType':
                selected = areaSubscriptionStore.getState().selectedPropertyTypes;
                break;
        }

        _data.forEach(({ card, item: { id } }) => {
            if (isAllSelected && selected.includes(id)) return;
            card.click();
        });
    });

    areaSubscriptionStore.subscribe(
        ({ selectedProjectPhases, selectedPropertyTypes }) => {
            if (!selectAllToggle.current) return;
            let selectedArray: string[];

            switch (itemType) {
                case 'projectPhase': {
                    selectedArray = selectedProjectPhases;
                    break;
                }
                case 'propertyType': {
                    selectedArray = selectedPropertyTypes;
                    break;
                }
            }

            if (!selectedArray.length || selectedArray.length < _data.length) {
                selectAllToggle.current.checked = false;
            } else {
                selectAllToggle.current.checked = true;
            }

            _data.forEach(({ item, card }) => {
                const toggle = card.querySelector<HTMLInputElement>(
                    `[js-hook-${TOGGLE_INPUT_HOOK}]`,
                )!;
                toggle.checked = selectedArray.includes(item.id);
            });
        },
        ['selectedProjectPhases', 'selectedPropertyTypes'],
    );

    const ProjectPhasesListItemDivider = () => {
        return <div class="u-margin-top-2 u-margin-bottom-2 u-divider" />;
    };
    const PropertyTypesListItemDivider = () => {
        return <div class="u-margin-top-3" />;
    };
    const PropertyTypesListSectionDivider = () => {
        return <div class="u-margin-top-1 u-margin-bottom-3 u-divider" />;
    };

    const ListItemDivider =
        itemType === 'projectPhase' ? ProjectPhasesListItemDivider : PropertyTypesListItemDivider;
    const ListSectionDivider =
        itemType === 'projectPhase'
            ? ProjectPhasesListItemDivider
            : PropertyTypesListSectionDivider;

    const cardForDataItem = ({ children, ...item }: AreaSubscriptionListCardInterface) => {
        const card = (
            <AreaSubscriptionListCard
                {...{ ...item, selectable, ...(selectable && { onItemClick }) }}>
                {children}
            </AreaSubscriptionListCard>
        ) as HTMLButtonElement;

        const Card = () => card;
        const CardElement = () => (
            <Fragment>
                <Card />
                <ListItemDivider />
            </Fragment>
        );
        return { card, CardElement };
    };

    const ListHeaderItem = (data: AreaSubscriptionListHeaderInterface) => (
        <div class="area-subscription-list__section-header">
            <span class="area-subscription-list__section-header-item-counter">
                {data.itemsCount}
            </span>
            <div>
                <p class="u-body-normal-bold">{data.title}</p>
                {data.status ? (
                    <span class={`u-body-normal ${COLOR_CLASSES[data.status.color]}`}>
                        {data.status.label}
                    </span>
                ) : null}
            </div>
        </div>
    );

    useBindMethod(
        'setData',
        (data: AreaSubscriptionListProjectPhaseData | AreaSubscriptionListPropertyTypeData) => {
            _data = [];
            element.list.innerHTML = '';

            if (itemType === 'projectPhase') {
                data = data as AreaSubscriptionListProjectPhaseData;

                element.list.append(
                    ...data.map((item) => {
                        const { card, CardElement } = cardForDataItem(item);
                        _data.push({ card, item });

                        return <CardElement />;
                    }),
                );
            } else if (itemType === 'propertyType') {
                data = data as AreaSubscriptionListPropertyTypeData;

                data.forEach(({ header, listItems }) => {
                    if (!header || !listItems) return;
                    element.list.append(<ListHeaderItem {...header} />);
                    element.list.append(<ListItemDivider />);
                    element.list.append(
                        ...listItems.map((item) => {
                            const { card, CardElement } = cardForDataItem(item);
                            _data.push({ card, item });

                            return <CardElement />;
                        }),
                    );
                    element.list.append(<ListSectionDivider />);
                });
            }

            if (selectable && hasSelectAll) {
                if (_data.length <= 1) {
                    element.selectAllToggle?.classList.add(HIDDEN_CLASS);
                } else {
                    element.selectAllToggle?.classList.remove(HIDDEN_CLASS);
                }
            }
        },
    );

    return (
        <Fragment>
            <div
                class={`u-flex u-flex-between area-subscription-list__header${
                    itemType === 'propertyType' ? ' u-hidden' : ''
                }`}
                js-hook-area-subscription-list-header>
                <span class="u-h6 u-h4--md" js-hook-area-subscription-list-title />
                {selectable && hasSelectAll && (
                    <CheckboxElement
                        name="select-all-toggle"
                        id={`area-subscription-select-all-toggle-${itemType}`}
                        hook="area-subscription-list-select-all-toggle-inner"
                        bpdInputHook="area-subscription-list-select-all-toggle"
                        labelHook="area-subscription-list-select-all-toggle-label"
                        classes="area-subscription__checkbox"
                        label=""
                        hideError
                    />
                )}
            </div>
            <ListSectionDivider />
            <div
                class="u-flex u-flex-column area-subscription-list__items"
                js-hook-area-subscription-list
            />
        </Fragment>
    );
};

AreaSubscriptionList.props = {
    itemType: {
        type: String,
        required: true,
    },
    selectable: {
        type: Boolean,
    },
    hasSelectAll: {
        type: Boolean,
    },
    onItemClick: {
        type: Function,
    },
};

defineElement('area-subscription-list', AreaSubscriptionList);
